<script setup lang="ts">
import {computed, nextTick, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import {QBadge, QBtn, QCard, QCardSection, QCheckbox, QDialog, QInput, QItem, QItemSection, QList, QMenu, QTooltip} from "quasar";
import {useEventListener} from "@vueuse/core";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import FullCalendar, {
  CalendarApi,
  CalendarOptions,
  DateSelectInfo,
  DatesSetInfo,
  EventChangeInfo,
  EventClickInfo,
  EventInput
} from "@fullcalendar/vue3";
import timeGridPlugin from "@fullcalendar/vue3/timegrid";
import dayGridPlugin from "@fullcalendar/vue3/daygrid";
import interactionPlugin from "@fullcalendar/vue3/interaction";
import {contentFromSlot, formaCalendarOptions, useCalendarDarkMode, withFormaClass} from "@/utils/calendar/FullCalendarForma.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import Translator from "@/utils/common/Translator.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import TechnicianAppointmentUtils, {
  CalendarDateRange
} from "@/utils/common/TechnicianAppointmentUtils.ts";
import MyAppointmentsUtils, {AppointmentFilters} from "@/utils/common/MyAppointmentsUtils.ts";
import AppointmentFilterBar from "@/components/appointments/AppointmentFilterBar.vue";
import dayjs from "@/utils/dayjs";
import EditScreenField from "@/components/EditView/editScreens/EditScreenField.vue";
import FieldType from "@/utils/constants/FieldType.ts";
import TechnicianAppointmentStatus from "@/utils/constants/TechnicianAppointmentStatus.ts";
import {useConfirmEachApptChange} from "@/stores/nama-flags-store.ts";
import MetaData from "@/utils/common/MetaData.ts";
import OwnerType from "@/utils/constants/OwnerType.ts";
import PreferencesRequest from "@/utils/constants/PreferencesRequest.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import {ClassToValues} from "@/utils/presenters/interfaces.ts";
import {useRouteSecurityGuard} from "@/composables/useRouteSecurityGuard.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import CapabilityType from "@/utils/constants/CapabilityType.ts";
import RouteSecurity from "@/router/RouteSecurity.ts";
import ApplicableWhen from "@/utils/constants/ApplicableWhen.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";

type EntityReferenceData = namaTypes.EntityReferenceData;
type BaseEntityDTO = namaTypes.BaseEntityDTO;
type EditScreenField = namaTypes.EditScreenField;

useRouteSecurityGuard();

const {
  APPOINTMENT_ENTITY, CREW_ENTITY, DEPARTMENT_ENTITY, PROCEDURE_ENTITY,
  DEPARTMENT_FIELD_ID, PROCEDURE_FIELD_ID, FROM_DOC_FIELD_ID
} = TechnicianAppointmentUtils;

const canViewCrews = SecurityUtils.userCan(CREW_ENTITY, CapabilityType.ListView);
const crewsAccessDeniedMessage = canViewCrews ? "" : RouteSecurity.missingCapabilityMessageFor(CREW_ENTITY, CapabilityType.ListView);

const STATUS_FIELD_ID = "status";
const PERIODS_FIELD_ID = "details";
const PERIOD_TIME_FIELD_IDS = ["details.day", "details.fromTime", "details.toTime"];
const PERIOD_REMARK_FIELD_ID = "details.remark";

const missingCommitMessage = SecurityUtils.userCan(APPOINTMENT_ENTITY, CapabilityType.Commit)
  ? null : RouteSecurity.missingCapabilityMessageFor(APPOINTMENT_ENTITY, CapabilityType.Commit);

function fieldNotEditableMessage(fieldId: string): string {
  return Translator.translate('techApptFieldNotEditable').replace("{0}", Translator.translateId(fieldId));
}

function editBlockedReason(fieldIds: string[], applicableWhen: string = ApplicableWhen.UpdateMode): string | null {
  if (missingCommitMessage)
    return missingCommitMessage;
  const disabledFieldId = fieldIds.find(fieldId => !SecurityUtils.userCanEdit(APPOINTMENT_ENTITY, fieldId, applicableWhen));
  return disabledFieldId ? fieldNotEditableMessage(disabledFieldId) : null;
}

function periodRemovalBlockedReason(): string | null {
  if (!SecurityUtils.userCanDelete(APPOINTMENT_ENTITY))
    return RouteSecurity.missingCapabilityMessageFor(APPOINTMENT_ENTITY, CapabilityType.Delete);
  if (CurrentUserUtils.userCanNOTDeleteLine(APPOINTMENT_ENTITY, PERIODS_FIELD_ID, ApplicableWhen.UpdateMode))
    return fieldNotEditableMessage(PERIODS_FIELD_ID);
  return editBlockedReason([STATUS_FIELD_ID]);
}

const translate = Translator.translate;
const refLabel = TechnicianAppointmentUtils.refLabel;
const darkMode = useCalendarDarkMode();
const {confirmEachApptChange} = useConfirmEachApptChange();
const isArabic = Translator.isArabic();
const dateLocale = isArabic ? "ar" : "en";

const SWITCHABLE_VIEWS = [
  {type: "timeGridDay", labelKey: "Day"},
  {type: "timeGridWeek", labelKey: "Week"},
  {type: "dayGridMonth", labelKey: "Month"}
];
const DEFAULT_VIEW = SWITCHABLE_VIEWS[1].type;

const errorStore = useErrorsStore();
const messagesStore = useMessagesStore();

const technicianPresenter = new QuestionFieldsPresenter(APPOINTMENT_ENTITY);

const departmentField: EditScreenField & { "namaClassTypeId": string } = {
  "namaClassTypeId": "EditScreenField",
  id: DEPARTMENT_FIELD_ID
}

const departmentFieldMetaData: FieldMetaData = {
  fieldId: DEPARTMENT_FIELD_ID,
  fieldType: FieldType.Reference,
  referencedEntityType: DEPARTMENT_ENTITY,
  detail: false,
  required: true,
  fieldInformation: {
    fieldId: DEPARTMENT_FIELD_ID,
    hasSearchContext: true,
  },
  enabled: true,
  id: DEPARTMENT_FIELD_ID
}

const procedureField: EditScreenField & { "namaClassTypeId": string } = {
  "namaClassTypeId": "EditScreenField",
  id: PROCEDURE_FIELD_ID
}

const procedureFieldMetaData: FieldMetaData = {
  fieldId: PROCEDURE_FIELD_ID,
  fieldType: FieldType.Reference,
  referencedEntityType: PROCEDURE_ENTITY,
  detail: false,
  required: true,
  fieldInformation: {
    fieldId: PROCEDURE_FIELD_ID,
    hasSearchContext: true,
  },
  enabled: true,
  id: PROCEDURE_FIELD_ID
}

const departmentRef = computed<EntityReferenceData | null>(() => technicianPresenter.record.value.departmentSection ?? null);
const procedureRef = computed<EntityReferenceData | null>(() => technicianPresenter.record.value.technicianProcedure ?? null);

const criteriaForField = (fieldId?: string) => TechnicianAppointmentUtils.criteriaForField(fieldId, departmentRef.value?.id);

technicianPresenter.fetchCriteriaFromFilterField = (_referencedEntityType?: string, fieldId?: string) => criteriaForField(fieldId);
technicianPresenter.getDefaultCriteria = (fieldId: string) => criteriaForField(fieldId);

watch([departmentRef, procedureRef], () => onFiltersChanged());

const fromDocField: EditScreenField & { "namaClassTypeId": string } = {
  "namaClassTypeId": "EditScreenField",
  id: FROM_DOC_FIELD_ID
}

const fromDocFieldMetaData = ref<FieldMetaData>();

const buildFromDocFieldMetaData = (entityMetaData?: namaTypes.NaMaMetaData): FieldMetaData => ({
  fieldId: FROM_DOC_FIELD_ID,
  fieldType: FieldType.Genericreference,
  allowedValues: [CommonConstants.GENERIC_REF_ALL_DOCS],
  detail: false,
  id: FROM_DOC_FIELD_ID,
  ...(entityMetaData ? MetaData.findFieldMetadata(FROM_DOC_FIELD_ID, entityMetaData) : {}),
  required: true,
  enabled: true
});

MetaData.fetchPreferences({
  owner: {ownerType: OwnerType.Entity, ownerId: APPOINTMENT_ENTITY},
  properties: [PreferencesRequest.META_DATA]
}, (preferences: namaTypes.UIPreferences) => fromDocFieldMetaData.value = buildFromDocFieldMetaData(preferences.metaData))
  .catch(() => fromDocFieldMetaData.value = buildFromDocFieldMetaData());

const fromDocRef = computed<EntityReferenceData | null>(() => technicianPresenter.record.value.fromDoc ?? null);
const router = useRouter();

const PREFILL_PARAMS = ['fromDocType', 'fromDocCodeOrId', 'technicianProcedure', 'departmentSection'];

const creatorProps = defineProps<{ fromDoc?: EntityReferenceData }>();

if (creatorProps.fromDoc)
  technicianPresenter.record.value = emptyRecord();
else
  prefillFromQuery(useRoute().query as Record<string, string>);

function emptyRecord(): BaseEntityDTO {
  return creatorProps.fromDoc ? {fromDoc: creatorProps.fromDoc} : {};
}

async function prefillFromQuery(query: Record<string, string>) {
  const {fromDocType, fromDocCodeOrId, technicianProcedure, departmentSection} = query;
  const [department, procedure, fromDoc] = await Promise.all([
    departmentSection ? findRefByCode(DEPARTMENT_ENTITY, departmentSection, DEPARTMENT_FIELD_ID) : undefined,
    technicianProcedure ? findRefByCode(PROCEDURE_ENTITY, technicianProcedure, PROCEDURE_FIELD_ID) : undefined,
    fromDocType && fromDocCodeOrId ? findRefByCode(fromDocType, fromDocCodeOrId, FROM_DOC_FIELD_ID) : undefined
  ]);
  const record = {...technicianPresenter.record.value};
  if (department)
    record.departmentSection = department;
  if (procedure)
    record.technicianProcedure = procedure;
  if (fromDoc)
    record.fromDoc = fromDoc;
  technicianPresenter.record.value = record;
  const remainingQuery = {...query};
  PREFILL_PARAMS.forEach(param => delete remainingQuery[param]);
  if (Object.keys(remainingQuery).length !== Object.keys(query).length)
    await router.replace({query: remainingQuery});
}

function findRefByCode(entityType: string, code: string, fieldId: string) {
  return EditScreenFieldUtils.findRefByCode(entityType, code, technicianPresenter.createSearchContextInfo(fieldId));
}

const ready = computed(() => !!(departmentRef.value?.id && procedureRef.value?.id));

const crewColorMap = new Map<string, string>();

const crewInfos = ref<namaTypes.DTOTechnicianCrewInfo[]>([]);
const crews = computed<EntityReferenceData[]>(() => crewInfos.value.flatMap(info => info.crew ? [info.crew] : []));
const crewIds = computed(() => crews.value.map(crew => crew.id).filter(Boolean) as string[]);
const EXCLUDED_FILTERS = [TechnicianAppointmentUtils.PROCEDURE_FIELD_ID];
const filters = ref<AppointmentFilters>({conditions: [], rules: [], matchAny: false});
const events = ref<EventInput[]>([]);
const busyEvents = ref<EventInput[]>([]);
const hiddenBusyEvents = ref<EventInput[]>([]);
const holidayEvents = ref<EventInput[]>([]);
const unavailabilityEvents = ref<EventInput[]>([]);
const hiddenCrews = ref<Set<string>>(new Set());

function isCrewVisible(id?: string): boolean {
  return !!id && !hiddenCrews.value.has(id);
}

function toggleCrew(id?: string) {
  if (!id)
    return;
  const next = new Set(hiddenCrews.value);
  if (next.has(id))
    next.delete(id);
  else
    next.add(id);
  hiddenCrews.value = next;
}

const calendarEvents = computed<EventInput[]>(() => {
  const visible = (e: EventInput) => {
    const crewId = (e.extendedProps as any)?.crew?.id as string | undefined;
    return !crewId || !hiddenCrews.value.has(crewId);
  };
  return [
    ...events.value.filter(visible),
    ...positionedBusyEvents.value.filter(visible),
    ...unavailabilityEvents.value.filter(visible),
    ...holidayEvents.value
  ];
});
const saving = ref(false);

const bookingRules = ref<namaTypes.DTOBookingRule[]>([]);
const validRange = ref<namaTypes.DTOTimeRange | null>(null);
const slotDuration = ref<string | null>(null);
const slotWindow = ref<namaTypes.DTOTimeRange | null>(null);
const businessHours = ref<any>(false);
let constraintsDeptId: string | null = null;
const calendarRef = ref<any>(null);
const visibleRange = ref<CalendarDateRange | null>(null);
const period = ref<CalendarDateRange | null>(null);
const viewType = ref(DEFAULT_VIEW);
const viewTitle = ref("");

const crewDialogOpen = ref(false);
const pendingSelection = ref<DateSelectInfo | null>(null);

const crewFilter = ref("");
const visibleCrews = computed<EntityReferenceData[]>(() => crews.value.filter(c => isCrewVisible(c.id)));
const selectableCrews = computed<EntityReferenceData[]>(() => {
  const sel = pendingSelection.value;
  if (!sel)
    return visibleCrews.value;
  return visibleCrews.value.filter(c =>
    !TechnicianAppointmentUtils.crewBlockedBetween(c.id, sel.start, sel.end, unavailabilityEvents.value));
});
const filteredCrews = computed<EntityReferenceData[]>(() => {
  const q = crewFilter.value.trim().toLowerCase();
  if (!q)
    return selectableCrews.value;
  return selectableCrews.value.filter(c =>
    refLabel(c).toLowerCase().includes(q) || (c.code ?? "").toLowerCase().includes(q));
});

const deleteDialogOpen = ref(false);
const eventToDelete = ref<string | null>(null);

interface PendingLineMove {
  lineId: string;
  start: Date;
  end: Date;
}

interface PendingAppointment {
  appointmentId: string;
  apptCode?: string;
  currentStatus?: string;
  presetStatus?: string;
  reason?: string;
  moves: Map<string, PendingLineMove>;
  removals: Set<string>;
}

const pending = ref(new Map<string, PendingAppointment>());
const savingStatus = ref(false);

const pendingAppointmentIds = computed(() => new Set(pending.value.keys()));
const isDirty = computed(() => pending.value.size > 0);
const pendingPeriodCount = computed(() =>
  [...pending.value.values()].reduce((total, entry) => total + entry.moves.size + entry.removals.size, 0));

const dirtySummary = computed(() => {
  const appointments = pending.value.size;
  const periods = pendingPeriodCount.value;
  const appointmentsPart = appointments === 1
    ? translate('techApptOneAppointment')
    : translate('techApptCountAppointments').replace("{0}", appointments + "");
  if (!periods)
    return appointmentsPart;
  const periodsPart = periods === 1
    ? translate('techApptOnePeriod')
    : translate('techApptCountPeriods').replace("{0}", periods + "");
  return periodsPart + " · " + appointmentsPart;
});

function colorFor(crew?: EntityReferenceData): string {
  return TechnicianAppointmentUtils.colorFor(crew, crewColorMap);
}

function getCalendarApi(): CalendarApi | undefined {
  return calendarRef.value?.getApi?.();
}

async function onFiltersChanged() {
  events.value = [];
  busyEvents.value = [];
  unavailabilityEvents.value = [];
  crewInfos.value = [];
  hiddenCrews.value = new Set();
  crewColorMap.clear();
  getCalendarApi()?.unselect();
  const deptId = departmentRef.value?.id ?? null;
  if (deptId !== constraintsDeptId) {
    clearBookingConstraints();
    constraintsDeptId = deptId;
    if (deptId)
      await loadBookingConstraints(deptId);
  }
  if (!ready.value)
    return;
  await loadCrews();
  await loadCalendarEvents();
}

async function loadCrews() {
  crewInfos.value = await TechnicianAppointmentUtils.loadCrews(departmentRef.value?.id, procedureRef.value?.id);
  crews.value.forEach(colorFor);
}


/** The whole shown week, before the filter period narrows it. */
function visibleWeekRange(): CalendarDateRange | null {
  const api = getCalendarApi();
  const start = visibleRange.value?.start ?? api?.view?.activeStart;
  const end = visibleRange.value?.end ?? api?.view?.activeEnd;
  return (start && end) ? {start, end} : null;
}

function currentRange(): CalendarDateRange | null {
  const week = visibleWeekRange();
  return week ? MyAppointmentsUtils.rangeWithin(week, filters.value.fromDate, filters.value.toDate) : null;
}

function filtersHideEvents(): boolean {
  const {conditions, rules, fromDate, toDate} = filters.value;
  return !!(conditions.length || rules.length || fromDate || toDate);
}

let latestCalendarFetchId = 0;

async function loadCalendarEvents() {
  const fetchId = ++latestCalendarFetchId;
  const week = visibleWeekRange();
  const unfiltered = await TechnicianAppointmentUtils.loadCalendarEvents(crewIds.value, week, false);
  // A hidden booking still owns its slot, and the period can hide a day that stays visible and
  // draggable, so the drag guard needs the unfiltered week.
  // ponytail: one extra request, only while a filter is active.
  const {conditions, rules, matchAny} = filters.value;
  const shown = filtersHideEvents()
    ? await TechnicianAppointmentUtils.loadCalendarEvents(crewIds.value, currentRange(), false,
      TechnicianAppointmentUtils.filterCriteria(conditions, rules, matchAny))
    : unfiltered;
  if (fetchId !== latestCalendarFetchId)
    return;
  busyEvents.value = shown.periods.map(period => TechnicianAppointmentUtils.busyEventFor(period, crewColorMap));
  const shownLineIds = new Set(shown.periods.map(period => period.lineId));
  hiddenBusyEvents.value = unfiltered.periods.filter(period => !shownLineIds.has(period.lineId))
    .map(period => TechnicianAppointmentUtils.busyEventFor(period, crewColorMap));
  unavailabilityEvents.value = unfiltered.blocks.flatMap(unavailabilityEventFor);
}

function unavailabilityEventFor(block: namaTypes.DTOCrewBlock): EventInput[] {
  const crew = crews.value.find(candidate => candidate.id === block.crewId);
  return crew ? [TechnicianAppointmentUtils.unavailabilityEventFor(block, crew, crewColorMap)] : [];
}

watch(filters, () => loadCalendarEvents());
watch(() => [filters.value.fromDate, filters.value.toDate], () => {
  const target = MyAppointmentsUtils.periodTarget(visibleRange.value, filters.value.fromDate, filters.value.toDate);
  if (target)
    getCalendarApi()?.gotoDate(target);
});

function clearBookingConstraints() {
  bookingRules.value = [];
  validRange.value = null;
  slotDuration.value = null;
  slotWindow.value = null;
  businessHours.value = false;
  holidayEvents.value = [];
}

function rebuildHolidayEvents(rangeStart?: Date, rangeEnd?: Date) {
  const api = getCalendarApi();
  holidayEvents.value = TechnicianAppointmentUtils.buildHolidayEvents(
    rangeStart ?? api?.view?.activeStart,
    rangeEnd ?? api?.view?.activeEnd,
    bookingRules.value);
}

async function onDatesSet(arg: DatesSetInfo) {
  rebuildHolidayEvents(arg.start, arg.end);
  visibleRange.value = {start: arg.start, end: arg.end};
  period.value = {start: arg.view.currentStart, end: arg.view.currentEnd};
  viewType.value = arg.view.type;
  viewTitle.value = arg.view.title;
  if (crews.value.length)
    await loadCalendarEvents();
}

async function onEventChange(arg: EventChangeInfo) {
  const updatedEvent = arg.event;
  const props: any = updatedEvent.extendedProps ?? {};
  if (!props.busy) {
    const eventToUpdate = events.value.find(e => e.id == updatedEvent.id);
    if (!eventToUpdate)
      return;
    eventToUpdate.start = updatedEvent.start;
    eventToUpdate.end = updatedEvent.end;
    return;
  }

  if (props.locked) {
    arg.revert();
    errorStore.pushErrorStr(translate('techApptExecutedCannotChange'));
    return;
  }

  const moveBlockedReason = editBlockedReason([...PERIOD_TIME_FIELD_IDS, STATUS_FIELD_ID]);
  if (moveBlockedReason) {
    arg.revert();
    errorStore.pushErrorStr(moveBlockedReason);
    return;
  }

  const start = updatedEvent.start;
  const end = updatedEvent.end;
  if (!start || !end || !props.appointmentId || !props.lineId) {
    arg.revert();
    errorStore.pushErrorStr(translate('techApptPeriodNotIdentified'));
    return;
  }

  if (overlapsAnotherPeriod(props.crew?.id, start, end, updatedEvent.id)) {
    arg.revert();
    errorStore.pushErrorStr(translate('techApptPeriodOverlaps'));
    return;
  }

  stagePeriodMove(props, start, end);
}

function overlapsAnotherPeriod(crewId: string | undefined, start: Date, end: Date, movedEventId: string): boolean {
  const drawn = getCalendarApi()?.getEvents() ?? [];
  return TechnicianAppointmentUtils.overlapsAnotherPeriod(crewId, start, end, movedEventId,
    [...drawn, ...hiddenBusyEvents.value]);
}

function pendingEntryFor(props: any): PendingAppointment {
  const existing = pending.value.get(props.appointmentId);
  if (existing)
    return existing;
  const created: PendingAppointment = {
    appointmentId: props.appointmentId,
    apptCode: props.apptCode,
    currentStatus: props.status,
    moves: new Map(),
    removals: new Set()
  };
  pending.value.set(props.appointmentId, created);
  return created;
}

function touchPending() {
  pending.value = new Map(pending.value);
}

function defaultStatusOf(entry: PendingAppointment): string | undefined {
  return entry.presetStatus
    ?? (entry.moves.size || entry.removals.size ? TechnicianAppointmentStatus.Rescheduled : entry.currentStatus);
}

function hasStagedWork(entry: PendingAppointment): boolean {
  return !!entry.presetStatus || !!entry.moves.size || !!entry.removals.size;
}

function afterStaging(entry: PendingAppointment) {
  touchPending();
  if (!hasStagedWork(entry))
    return;
  if (confirmEachApptChange.value)
    void commitImmediately(entry);
}

function stagePeriodMove(props: any, start: Date, end: Date) {
  const entry = pendingEntryFor(props);
  entry.moves.set(props.lineId, {lineId: props.lineId, start, end});
  afterStaging(entry);
}

function pendingMoveFor(event: EventInput): PendingLineMove | undefined {
  const props: any = event.extendedProps ?? {};
  return props.lineId ? pending.value.get(props.appointmentId)?.moves.get(props.lineId) : undefined;
}

const positionedBusyEvents = computed<EventInput[]>(() => busyEvents.value.map(event => {
  if (isPendingRemoval(event))
    return {
      ...event, editable: false,
      className: (event.className as string) + " tech-cal__busy--removed",
      extendedProps: {...event.extendedProps as any, removed: true}
    };
  const move = pendingMoveFor(event);
  return move ? {...event, start: move.start, end: move.end} : event;
}));

function isPendingRemoval(event: EventInput): boolean {
  const props: any = event.extendedProps ?? {};
  return !!props.lineId && !!pending.value.get(props.appointmentId)?.removals.has(props.lineId);
}

function stagePeriodRemoval(props: any) {
  closeContextMenu();
  const entry = pendingEntryFor(props);
  entry.moves.delete(props.lineId);
  entry.removals.add(props.lineId);
  entry.presetStatus = TechnicianAppointmentStatus.Rescheduled;
  afterStaging(entry);
}

function restorePeriod(props: any) {
  closeContextMenu();
  const entry = pending.value.get(props.appointmentId);
  if (!entry)
    return;
  entry.removals.delete(props.lineId);
  if (!entry.moves.size && !entry.removals.size)
    pending.value.delete(props.appointmentId);
  touchPending();
}

function togglePendingAppointment(props: any) {
  const staged = pending.value.get(props.appointmentId);
  if (!staged) {
    afterStaging(pendingEntryFor(props));
    return;
  }
  const droppedMoves = staged.moves.size + staged.removals.size;
  pending.value.delete(props.appointmentId);
  touchPending();
  if (droppedMoves)
    messagesStore.pushMessageStr(translate('techApptPendingChangesDropped'));
}


interface ContextAction {
  key: string;
  label: string;
  icon: string;
  danger?: boolean;
  blocked?: boolean;
  run: () => void;
}

function blockedBySecurity(action: ContextAction, blockedReason: string | null): ContextAction {
  if (!blockedReason)
    return action;
  return {
    ...action, blocked: true,
    run: () => {
      closeContextMenu();
      errorStore.pushErrorStr(blockedReason);
    }
  };
}

const contextProps = ref<any>(null);
const contextMenuOpen = ref(false);
const contextAnchor = ref<HTMLElement | null>(null);

function stampEventId(info: { el: HTMLElement; event: { id: string } }) {
  info.el.dataset.apptEventId = info.event.id;
}

function moveAnchorTo(x: number, y: number) {
  const anchor = contextAnchor.value;
  if (!anchor)
    return;
  anchor.style.left = x + "px";
  anchor.style.top = y + "px";
}

const reasonEntry = ref<PendingAppointment | null>(null);
const reasonMenuOpen = ref(false);
const reasonDraft = ref("");

function openReasonPopover(entry: PendingAppointment) {
  closeContextMenu();
  reasonDraft.value = entry.reason ?? "";
  reasonEntry.value = entry;
  void nextTick(() => reasonMenuOpen.value = true);
}

function finishReason() {
  const entry = reasonEntry.value;
  reasonEntry.value = null;
  if (!entry || !pending.value.has(entry.appointmentId))
    return;
  entry.reason = reasonDraft.value.trim() || undefined;
  if (!entry.reason && !hasStagedWork(entry))
    pending.value.delete(entry.appointmentId);
  else if (confirmEachApptChange.value)
    void commitImmediately(entry);
  touchPending();
}

const calendarWrapper = ref<HTMLElement | null>(null);

let swallowCalendarClick = false;

function dismissesOwnMenu(mouseEvent: MouseEvent): boolean {
  return mouseEvent.button === 0
    && (contextMenuOpen.value || reasonMenuOpen.value)
    && !!calendarWrapper.value?.contains(mouseEvent.target as Node);
}

function onDocumentPointerDown(mouseEvent: MouseEvent) {
  swallowCalendarClick = dismissesOwnMenu(mouseEvent);
  if (swallowCalendarClick) {
    mouseEvent.stopPropagation();
    mouseEvent.preventDefault();
  }
}

function onDocumentClick(mouseEvent: MouseEvent) {
  if (!swallowCalendarClick)
    return;
  swallowCalendarClick = false;
  mouseEvent.stopPropagation();
  mouseEvent.preventDefault();
}

useEventListener(document, "mousedown", onDocumentPointerDown, true);
useEventListener(document, "click", onDocumentClick, true);

function onCalendarContextMenu(mouseEvent: MouseEvent) {
  const el = (mouseEvent.target as HTMLElement)?.closest?.("[data-appt-event-id]") as HTMLElement | null;
  const found = el?.dataset.apptEventId
    ? getCalendarApi()?.getEventById(el.dataset.apptEventId)
    : null;
  const props: any = found?.extendedProps ?? {};
  if (!found || (props.readonly && !props.unavailable) || (props.busy && !props.appointmentId))
    return;
  mouseEvent.preventDefault();

  moveAnchorTo(mouseEvent.clientX, mouseEvent.clientY);
  // Right-clicking a second event while the menu is open has to close and reopen it, otherwise
  // QMenu keeps the position it measured the first time.
  contextMenuOpen.value = false;
  void nextTick(() => {
    contextProps.value = {...props, eventId: found.id};
    contextMenuOpen.value = true;
  });
}

/** `contextProps` survives the close: QMenu's hide transition finishes after the next open has
 *  already run, and clearing it here would blank the menu that just opened. */
function closeContextMenu() {
  contextMenuOpen.value = false;
}

function stageWithStatus(props: any, status: string) {
  const entry = pendingEntryFor(props);
  entry.presetStatus = status;
  closeContextMenu();
  afterStaging(entry);
}

function openRecordAction(link?: string): ContextAction[] {
  if (!link)
    return [];
  return [{
    key: "open", icon: "open_in_new",
    label: translate('techApptOpenRecord'),
    run: () => {
      window.open(link, "_blank");
      closeContextMenu();
    }
  }];
}

const contextActions = computed<ContextAction[]>(() => {
  const props = contextProps.value;
  if (!props)
    return [];
  if (props.unavailable)
    return openRecordAction(TechnicianAppointmentUtils.linkToUnavailability(props.documentId));
  if (!props.busy)
    return [
      blockedBySecurity({
        key: "save-period", icon: "save",
        label: translate('techApptSavePeriod'),
        run: () => {
          closeContextMenu();
          void saveDrawnPeriod(props.eventId);
        }
      }, missingCommitMessage),
      {
        key: "delete-new-period", icon: "delete_outline", danger: true,
        label: translate('techApptDeletePeriod'),
        run: () => {
          closeContextMenu();
          askDeletePeriod(props.eventId);
        }
      }
    ];

  const actions: ContextAction[] = [];
  const statusAction = (status: string, label: string, icon: string, danger?: boolean): ContextAction => blockedBySecurity({
    key: status, label, icon, danger, run: () => stageWithStatus(props, status)
  }, editBlockedReason([STATUS_FIELD_ID]));

  if (!props.locked) {
    actions.push(statusAction(TechnicianAppointmentStatus.Cancelled,
      translate('techApptCancelAppointment'), "event_busy", true));
    actions.push(statusAction(TechnicianAppointmentStatus.NoShow,
      translate('techApptMarkNoShow'), "person_off"));
    actions.push(statusAction(TechnicianAppointmentStatus.Executed,
      translate('techApptMarkExecuted'), "task_alt"));
    if (props.lineId)
      actions.push(props.removed
        ? {
          key: "restore-period", icon: "undo",
          label: translate('techApptUndoPeriodDeletion'),
          run: () => restorePeriod(props)
        }
        : blockedBySecurity({
          key: "delete-period", icon: "delete_outline", danger: true,
          label: translate('techApptDeletePeriod'),
          run: () => stagePeriodRemoval(props)
        }, periodRemovalBlockedReason()));
    actions.push(blockedBySecurity({
      key: "reason", icon: "edit_note",
      label: translate('techApptAddReason'),
      run: () => openReasonPopover(pendingEntryFor(props))
    }, editBlockedReason([PERIOD_REMARK_FIELD_ID])));
  }

  if (pending.value.has(props.appointmentId))
    actions.push({
      key: "unstage", icon: "undo",
      label: translate('techApptRemoveFromPending'),
      run: () => {
        closeContextMenu();
        togglePendingAppointment(props);
      }
    });

  actions.push(...openRecordAction(TechnicianAppointmentUtils.linkToAppointment(props.appointmentId)));

  return actions;
});

function discardPendingChanges() {
  pending.value = new Map();
}

interface PlannedChange {
  entry: PendingAppointment;
  status?: ClassToValues<TechnicianAppointmentStatus>;
  reason?: string;
}

function plannedChanges(entries: PendingAppointment[]): PlannedChange[] {
  return entries.map(entry => ({entry, status: defaultStatusOf(entry), reason: entry.reason}));
}

async function commitImmediately(entry: PendingAppointment) {
  await commitChanges(plannedChanges([entry]));
  pending.value.delete(entry.appointmentId);
  touchPending();
}

async function commitChanges(plan: PlannedChange[]) {
  savingStatus.value = true;
  let saved = 0;
  let failed = 0;
  try {
    const results = await TechnicianAppointmentUtils.rescheduleAppointments(plan.map(toAppointmentChange));
    for (const result of results) {
      if (result.pendingApproval) {
        reportSentForApproval(result);
        pending.value.delete(result.appointmentId!);
      } else if (result.saved) {
        pending.value.delete(result.appointmentId!);
        saved++;
      } else {
        failed++;
        reportFailedChange(result);
      }
    }
  } finally {
    savingStatus.value = false;
    touchPending();
  }

  if (saved)
    messagesStore.pushMessageStr(saved === 1
      ? translate('techApptOneUpdated')
      : translate('techApptCountUpdated').replace("{0}", saved + ""));
  if (failed)
    errorStore.pushErrorStr(failed === 1
      ? translate('techApptOneNotSaved')
      : translate('techApptCountNotSaved').replace("{0}", failed + ""));

  void loadCalendarEvents();
}

function toAppointmentChange(change: PlannedChange): namaTypes.DTOAppointmentChange {
  return {
    appointmentId: change.entry.appointmentId,
    status: change.status,
    reason: change.reason,
    moves: [...change.entry.moves.values()].map(move => ({
      lineId: move.lineId,
      start: TechnicianAppointmentUtils.encodeDateTime(move.start),
      end: TechnicianAppointmentUtils.encodeDateTime(move.end)
    })),
    removedLineIds: [...change.entry.removals]
  };
}

function reportSentForApproval(result: namaTypes.DTOAppointmentChangeResult) {
  const apptCode = pending.value.get(result.appointmentId!)?.apptCode;
  messagesStore.pushMessageStr(translate('techApptSentForApproval') + (apptCode ? " " + apptCode : ""));
}

function reportFailedChange(result: namaTypes.DTOAppointmentChangeResult) {
  const apptCode = pending.value.get(result.appointmentId!)?.apptCode;
  if (result.errorKey)
    errorStore.pushErrorStr(translate(result.errorKey) + (apptCode ? " " + apptCode : ""));
  else
    errorStore.pushErrors(result.result?.errors ?? []);
}

async function loadBookingConstraints(deptId: string) {
  const calendar = await TechnicianAppointmentUtils.fetchBookingCalendar([deptId]);
  bookingRules.value = calendar.rules ?? [];
  validRange.value = calendar.validRange ?? null;
  slotDuration.value = calendar.slotDuration ?? null;
  businessHours.value = TechnicianAppointmentUtils.toCalendarBusinessHours(calendar.businessHours);
  slotWindow.value = calendar.slotWindow ?? null;
  rebuildHolidayEvents();
}

function selectAllow(info: { start: Date; end: Date }): boolean {
  return TechnicianAppointmentUtils.isSelectionAllowed(info, bookingRules.value);
}

function onDateSelect(info: DateSelectInfo) {
  if (!ready.value) {
    getCalendarApi()?.unselect();
    return;
  }
  const drawBlockedReason = editBlockedReason(PERIOD_TIME_FIELD_IDS, ApplicableWhen.NewMode);
  if (drawBlockedReason) {
    getCalendarApi()?.unselect();
    errorStore.pushErrorStr(drawBlockedReason);
    return;
  }
  if (!fromDocRef.value?.id) {
    getCalendarApi()?.unselect();
    errorStore.pushErrorStr(translate('techApptPickFromDoc'));
    return;
  }
  if (!visibleCrews.value.length) {
    getCalendarApi()?.unselect();
    errorStore.pushErrorStr(translate('techApptPickCrew'));
    return;
  }
  pendingSelection.value = info;
  crewFilter.value = "";
  if (!selectableCrews.value.length) {
    pendingSelection.value = null;
    getCalendarApi()?.unselect();
    errorStore.pushErrorStr(translate('techApptCrewUnavailableInPeriod'));
    return;
  }
  if (selectableCrews.value.length === 1) {
    chooseCrew(selectableCrews.value[0]);
    return;
  }
  crewDialogOpen.value = true;
}

function chooseCrew(crew: EntityReferenceData) {
  const sel = pendingSelection.value;
  crewDialogOpen.value = false;
  pendingSelection.value = null;
  getCalendarApi()?.unselect();
  if (!sel)
    return;
  const currentCrewId = (events.value[0]?.extendedProps as any)?.crew?.id;
  const switchingCrew = !!currentCrewId && currentCrewId !== crew.id;
  const base = switchingCrew ? [] : events.value;
  if (switchingCrew)
    messagesStore.pushMessageStr(
      translate('techApptCrewChangedPeriodsRemoved')
    );
  const newEvent: EventInput = {
    id: "new-" + Date.now() + "-" + Math.random().toString(36).slice(2),
    title: refLabel(crew),
    start: sel.start,
    editable: true,
    end: sel.end,
    color: colorFor(crew),
    extendedProps: {crew}
  };
  events.value = [...base, newEvent];
}

function cancelSelect() {
  crewDialogOpen.value = false;
  pendingSelection.value = null;
  getCalendarApi()?.unselect();
}

function onEventClick(arg: EventClickInfo) {
  const props: any = arg.event.extendedProps ?? {};
  if (props.readonly)
    return;
  if (props.busy) {
    if (props.locked)
      messagesStore.pushMessageStr(translate('techApptExecutedCannotChange'));
    return;
  }
  askDeletePeriod(arg.event.id);
}

function askDeletePeriod(eventId: string) {
  eventToDelete.value = eventId;
  deleteDialogOpen.value = true;
}

function confirmDelete() {
  const id = eventToDelete.value;
  deleteDialogOpen.value = false;
  eventToDelete.value = null;
  if (!id) return;
  events.value = events.value.filter(e => e.id !== id);
}

const createBlockedReason = computed<string | null>(() => {
  if (missingCommitMessage)
    return missingCommitMessage;
  if (!ready.value)
    return translate('techApptPickDeptAndProcedureFirst');
  if (!events.value.length)
    return translate('techApptDrawAtLeastOnePeriod');
  if (!fromDocRef.value?.id)
    return translate('techApptPickFromDoc');
  return null;
});

const saveBlockedReason = computed<string | null>(() => isDirty.value ? missingCommitMessage : createBlockedReason.value);

const saveButtonLabel = computed(() =>
  isDirty.value ? translate('saveChanges') : Translator.translateId('save'));

async function save() {
  // Read before the commit empties the buffer.
  const hadPendingChanges = isDirty.value;
  if (hadPendingChanges)
    await commitChanges(plannedChanges([...pending.value.values()]));
  if (events.value.length || !hadPendingChanges)
    await createAppointment(events.value);
}

function saveDrawnPeriod(eventId: string) {
  const event = events.value.find(e => e.id === eventId);
  return event ? createAppointment([event]) : Promise.resolve();
}

async function createAppointment(fromEvents: EventInput[]) {
  if (createBlockedReason.value) {
    errorStore.pushErrorStr(createBlockedReason.value);
    return;
  }
  saving.value = true;
  try {
    const created: any = await NamaCommon.namaFetch(NamaURLS.CREATE_NEW, {
      params: {entityType: APPOINTMENT_ENTITY},
      data: {record: {entityType: APPOINTMENT_ENTITY}}
    });
    if (!created || !NamaCommon.isSuccess(created)) {
      errorStore.pushErrors(NamaCommon.extractErrorsFromResponse(created) ?? []);
      return;
    }

    const record: any = created.data;
    record.departmentSection = departmentRef.value;
    record.technicianProcedure = procedureRef.value;

    record.fromDoc = {
      entityType: fromDocRef.value?.entityType,
      id: fromDocRef.value?.id,
      code: fromDocRef.value?.code
    };
    record.details = TechnicianAppointmentUtils.buildDetailLines(fromEvents);


    const response: any = await NamaCommon.namaFetch(NamaURLS.SAVE_AND_COMMIT, {
      params: {entityType: APPOINTMENT_ENTITY, fromAutoSaveWithInput: false},
      data: record
    });

    if (NamaCommon.isSuccess(response)) {
      const saved = response.data as BaseEntityDTO;
      messagesStore.pushMessageStr(
        translate('techApptCreated') + (saved?.code ? " " + saved.code : "")
      );

      const consumed = new Set(fromEvents.map(e => e.id));
      events.value = events.value.filter(e => !consumed.has(e.id));
      void loadCalendarEvents();
    } else {
      errorStore.pushErrors(NamaCommon.extractErrorsFromResponse(response) ?? []);
    }
  } finally {
    saving.value = false;
  }
}

function addNew() {
  technicianPresenter.record.value = emptyRecord();
}

const isTimeGrid = computed(() => viewType.value.startsWith("timeGrid"));

const periodContainsToday = computed(() => {
  if (!period.value)
    return false;
  const now = Date.now();
  return now >= new Date(period.value.start).getTime() && now < new Date(period.value.end).getTime();
});

const periodMeta = computed(() => {
  if (!period.value)
    return "";
  const start = dayjs(period.value.start);
  if (viewType.value === "dayGridMonth")
    return start.format("YYYY-MM");
  if (viewType.value === "timeGridDay")
    return start.format("YYYY-MM-DD");
  const week = "W" + start.isoWeek();
  return periodContainsToday.value ? week + " · " + translate("myAppointmentsCurrentWeek") : week;
});

function dayName(date: Date): string {
  return date.toLocaleDateString(dateLocale, {weekday: "short"});
}


function goPrevious() {
  getCalendarApi()?.prev();
}

function goNext() {
  getCalendarApi()?.next();
}

function goToday() {
  getCalendarApi()?.today();
}

function switchTo(type: string) {
  getCalendarApi()?.changeView(type);
}

function weekendClass(optionName: "dayCellClass" | "dayLaneClass") {
  return withFormaClass<{ dow: number }>(optionName, info => isWeekend(info.dow) && "appt-weekend");
}

function eventClassFor(pendingIds: Set<string>) {
  return (info: { event: { extendedProps: any } }) => {
    const appointmentId = info.event?.extendedProps?.appointmentId;
    return appointmentId && pendingIds.has(appointmentId) ? "tech-cal__busy--pending" : "";
  };
}

function isWeekend(dow: number): boolean {
  return dow === 5 || dow === 6;
}

// A fresh validRange object makes FullCalendar rebuild its dates and fire datesSet, whose handler updates the events and re-runs this computed.
const calendarValidRange = computed(() => TechnicianAppointmentUtils.toCalendarValidRange(validRange.value));

const calendarOptions = computed<CalendarOptions>(() => formaCalendarOptions({
  plugins: [timeGridPlugin, dayGridPlugin, interactionPlugin],
  initialView: DEFAULT_VIEW,
  locale: dateLocale,
  direction: isArabic ? "rtl" : "ltr",
  headerToolbar: false,
  selectable: true,
  selectMirror: true,
  allDaySlot: false,
  nowIndicator: true,
  height: "100%",
  expandRows: true,
  scrollTime: TechnicianAppointmentUtils.timeOfDay(slotWindow.value?.start) ?? "07:00:00",
  slotMinTime: TechnicianAppointmentUtils.timeOfDay(slotWindow.value?.start) ?? "06:00:00",
  slotMaxTime: TechnicianAppointmentUtils.timeOfDay(slotWindow.value?.end) ?? "24:00:00",
  slotDuration: slotDuration.value ?? "00:30:00",
  validRange: calendarValidRange.value,
  businessHours: businessHours.value,
  selectConstraint: businessHours.value ? "businessHours" : undefined,
  selectAllow: selectAllow,
  eventAllow: (span) => selectAllow(span),
  eventClass: eventClassFor(pendingAppointmentIds.value),
  eventDidMount: stampEventId,
  events: calendarEvents.value,
  dayHeaderContent: contentFromSlot,
  dayCellClass: weekendClass("dayCellClass"),
  dayLaneClass: weekendClass("dayLaneClass"),
  datesSet: onDatesSet,
  eventChange: onEventChange,
  select: onDateSelect,
  eventClick: onEventClick
}, darkMode.value));


</script>

<template>
  <div class="tech-cal appt-page appt-theme">
    <header class="appt-header">
      <div v-if="!creatorProps.fromDoc" class="appt-brand">
        <div class="appt-brand-badge">
          <NamaIcon default-icon="event" size="22px"/>
        </div>
        <div class="appt-brand-text">
          <div class="appt-title">{{ translate('technicianAppointmentCreator') }}</div>
          <p class="appt-subtitle">{{ translate('techApptCreatorSubtitle') }}</p>
        </div>
      </div>

      <div class="appt-actions">
        <q-btn
          unelevated
          class="appt-btn appt-btn--primary"
          :loading="saving || savingStatus"
          :disable="!!saveBlockedReason"
          @click="save"
        >
          <NamaIcon default-icon="save" size="16px" class="tw-size-4"/>
          <span>{{ saveButtonLabel }}</span>
          <q-badge v-if="isDirty" class="tech-cal__save-badge" :label="pending.size"/>
          <q-tooltip v-if="saveBlockedReason" anchor="bottom middle" self="top middle">
            {{ saveBlockedReason }}
          </q-tooltip>
        </q-btn>
        <q-btn
          unelevated
          class="appt-btn appt-btn--ghost"
          :disable="saving"
          @click="addNew"
        >
          <NamaIcon default-icon="nm-add-record" class="tw-size-4"/>
          <span>{{ Translator.translateId('newRecordShort') }}</span>
        </q-btn>
      </div>
    </header>

    <div class="appt-card tech-cal__fields">
      <EditScreenField class="appt-field tech-cal__field" :class="{ 'appt-field--active': departmentRef?.id }"
                       :do-not-apply-theme-layouts="true"
                       :presenter="technicianPresenter" :field="departmentField" :field-meta-data="departmentFieldMetaData"/>
      <EditScreenField class="appt-field tech-cal__field" :class="{ 'appt-field--active': procedureRef?.id }"
                       :do-not-apply-theme-layouts="true"
                       :presenter="technicianPresenter" :field="procedureField" :field-meta-data="procedureFieldMetaData"/>
      <EditScreenField v-if="fromDocFieldMetaData && !creatorProps.fromDoc" class="appt-field tech-cal__field tech-cal__field--wide" :class="{ 'appt-field--active': fromDocRef?.id }"
                       :do-not-apply-theme-layouts="true"
                       :presenter="technicianPresenter" :field="fromDocField" :field-meta-data="fromDocFieldMetaData"/>
    </div>

    <AppointmentFilterBar :crew-ids="crewIds" :exclude-keys="EXCLUDED_FILTERS" @change="filters = $event"/>

    <div class="appt-body">
      <section class="appt-calendar">
        <div class="appt-toolbar">
          <div class="appt-nav">
            <button type="button" class="appt-chip" @click="goToday">{{ translate('today') }}</button>
            <div class="appt-arrows">
              <button type="button" class="appt-arrow" @click="goPrevious">{{ '‹' }}</button>
              <button type="button" class="appt-arrow" @click="goNext">{{ '›' }}</button>
            </div>
          </div>

          <div class="appt-period">
            <div class="appt-period-title">{{ viewTitle }}</div>
            <div class="appt-period-meta">{{ periodMeta }}</div>
          </div>

          <div class="tech-cal__toolbar-end">
            <label class="tech-cal__opt">
              <q-checkbox v-model="confirmEachApptChange" dense size="xs" class="nama-checkbox tech-cal__opt-box" :title="translate('techApptConfirmEachChangeHint')"/>
              <span>{{ translate('techApptConfirmEachChange') }}</span>
            </label>
            <div class="appt-views">
              <button
                v-for="view in SWITCHABLE_VIEWS"
                :key="view.type"
                type="button"
                class="appt-view"
                :class="{ 'appt-view--active': viewType === view.type }"
                @click="switchTo(view.type)">
                {{ translate(view.labelKey) }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="isDirty && !confirmEachApptChange" class="tech-cal__dirty">
          <div class="tech-cal__dirty-badge">
            <NamaIcon default-icon="edit" size="15px"/>
          </div>
          <div class="tech-cal__dirty-text">
            <span class="tech-cal__dirty-count">{{ dirtySummary }}</span>
            <span class="tech-cal__dirty-hint">{{ translate('techApptUseSaveChangesHint') }}</span>
          </div>
          <div class="tech-cal__dirty-btns">
            <q-btn unelevated class="appt-btn appt-btn--ghost" :disable="savingStatus"
                   :label="translate('discard')" @click="discardPendingChanges"/>
          </div>
        </div>

        <div v-show="ready" ref="calendarWrapper" class="appt-fc" @contextmenu="onCalendarContextMenu">
          <FullCalendar ref="calendarRef" :options="calendarOptions" class="tech-cal__fc">
            <template #dayHeaderContent="arg">
              <div class="appt-dayhead">
                <div class="appt-dayhead-name" :class="{ 'appt-dayhead-name--today': arg.isToday }">
                  {{ dayName(arg.date) }}
                </div>
                <template v-if="isTimeGrid">
                  <div class="appt-dayhead-num" :class="{ 'appt-dayhead-num--today': arg.isToday }">
                    {{ arg.date.getDate() }}
                  </div>
                </template>
              </div>
            </template>
          </FullCalendar>
        </div>

        <div v-if="!ready" class="tech-cal__overlay">
          <NamaIcon default-icon="event_available" size="44px"/>
          <p>{{ translate('techApptPickDeptAndProcedureToStart') }}</p>
        </div>
      </section>

      <aside class="appt-side">
        <div class="appt-card">
          <div class="appt-card-head">
            <span>{{ translate('techApptAvailableCrews') }}</span>
            <q-badge v-if="canViewCrews && crews.length" rounded color="primary" :label="crews.length"/>
          </div>

          <div v-if="!canViewCrews" class="tech-cal__hint">
            <NamaIcon default-icon="lock" size="28px"/>
            <span>{{ crewsAccessDeniedMessage }}</span>
          </div>
          <div v-else-if="!ready" class="tech-cal__hint">
            <NamaIcon default-icon="groups" size="28px"/>
            <span>{{ translate('techApptPickDeptAndProcedureFirst') }}</span>
          </div>
          <div v-else-if="!crews.length" class="tech-cal__hint">
            <NamaIcon default-icon="search_off" size="28px"/>
            <span>{{ translate('techApptNoMatchingCrews') }}</span>
          </div>
          <div v-else class="tech-cal__crew-list">
            <div
              v-for="c in crews"
              :key="c.id"
              class="tech-cal__crew"
              :class="{ 'tech-cal__crew--off': !isCrewVisible(c.id) }"
              :style="{ '--crew-color': colorFor(c) }"
              role="checkbox"
              :aria-checked="isCrewVisible(c.id)"
              :title="translate('techApptToggleCrewPeriods')"
              @click="toggleCrew(c.id)">
              <q-checkbox
                dense
                :model-value="isCrewVisible(c.id)"
                class="tech-cal__crew-check"
                @update:model-value="toggleCrew(c.id)"
                @click.stop/>
              <span class="tech-cal__crew-dot"></span>
              <span class="tech-cal__crew-name">{{ refLabel(c) }}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>


    <div ref="contextAnchor" class="tech-cal__ctx-anchor"></div>
    <q-menu v-model="contextMenuOpen" :target="contextAnchor" anchor="bottom left" self="top left"
            class="tech-cal__ctx appt-theme">
      <div class="tech-cal__ctx-head">
        {{ contextProps?.apptCode || refLabel(contextProps?.crew) || translate('technicianAppointment') }}
      </div>
      <q-list dense>
        <q-item v-for="action in contextActions" :key="action.key" clickable
                class="tech-cal__ctx-item"
                :class="{ 'tech-cal__ctx-item--danger': action.danger, 'tech-cal__ctx-item--blocked': action.blocked }"
                @click="action.run">
          <q-item-section avatar class="tech-cal__ctx-icon">
            <NamaIcon :default-icon="action.icon" size="16px"/>
          </q-item-section>
          <q-item-section>{{ action.label }}</q-item-section>
        </q-item>
        <q-item v-if="!contextActions.length" class="tech-cal__ctx-empty">
          <q-item-section>{{ translate('techApptNoActions') }}</q-item-section>
        </q-item>
      </q-list>
    </q-menu>

    <q-menu v-model="reasonMenuOpen" :target="contextAnchor" anchor="bottom left" self="top left"
            class="tech-cal__ctx tech-cal__reason appt-theme" @hide="finishReason">
      <div class="tech-cal__ctx-head">{{ translate('techApptReasonForChange') }}</div>
      <q-input v-model="reasonDraft" dense outlined autofocus autogrow type="textarea"
               class="tech-cal__reason-input" :placeholder="translate('techApptTypeReason')"
               @keydown.enter.exact.prevent="reasonMenuOpen = false"/>
      <div class="tech-cal__reason-foot">
        <span class="tech-cal__reason-hint">{{ translate('techApptReasonSavedToHistory') }}</span>
        <q-btn unelevated class="appt-btn appt-btn--primary tech-cal__reason-ok"
               :label="Translator.translateId('ok')" @click="reasonMenuOpen = false"/>
      </div>
    </q-menu>

    <q-dialog v-model="crewDialogOpen" @escape-key="cancelSelect">
      <q-card class="tech-cal__dialog appt-theme">
        <div class="tech-cal__dialog-head">
          <div class="tech-cal__dialog-badge">
            <NamaIcon default-icon="groups" size="18px"/>
          </div>
          <div class="tech-cal__dialog-heading">
            <div class="tech-cal__dialog-title">{{ translate('techApptChooseCrewForPeriod') }}</div>
            <div class="tech-cal__dialog-sub">{{ translate('techApptPeriodAddedAfterPick') }}</div>
          </div>
        </div>
        <q-card-section v-if="crews.length" class="tech-cal__dialog-search">
          <q-input
            v-model="crewFilter"
            dense
            outlined
            clearable
            autofocus
            :placeholder="translate('techApptSearchCrews')"
          >
            <template #prepend>
              <NamaIcon default-icon="search" size="18px"/>
            </template>
          </q-input>
        </q-card-section>
        <q-card-section class="tech-cal__dialog-body">
          <q-list v-if="filteredCrews.length" class="tech-cal__dialog-list">
            <q-item v-for="c in filteredCrews" :key="c.id" clickable class="tech-cal__dialog-item" @click="chooseCrew(c)">
              <q-item-section avatar>
                <span class="tech-cal__crew-dot" :style="{ '--crew-color': colorFor(c) }"></span>
              </q-item-section>
              <q-item-section>{{ refLabel(c) }}</q-item-section>
              <q-item-section side>
                <NamaIcon default-icon="add" size="18px"/>
              </q-item-section>
            </q-item>
          </q-list>
          <div v-else class="tech-cal__hint">
            <NamaIcon default-icon="search_off" size="28px"/>
            <span>{{
                crews.length ? translate('techApptNoCrewsMatchSearch') : translate('techApptNoMatchingCrews')
              }}</span>
          </div>
        </q-card-section>
        <div class="tech-cal__dialog-foot">
          <div class="tech-cal__dialog-btns">
            <q-btn unelevated class="appt-btn appt-btn--ghost" :label="translate('cancel')" @click="cancelSelect"/>
          </div>
        </div>
      </q-card>
    </q-dialog>

    <q-dialog v-model="deleteDialogOpen">
      <q-card class="tech-cal__dialog appt-theme">
        <div class="tech-cal__dialog-head">
          <div class="tech-cal__dialog-badge tech-cal__dialog-badge--danger">
            <NamaIcon default-icon="delete_outline" size="18px"/>
          </div>
          <div class="tech-cal__dialog-heading">
            <div class="tech-cal__dialog-title">{{ translate('techApptDeletePeriodQuestion') }}</div>
            <div class="tech-cal__dialog-sub">{{ translate('techApptPeriodNotSaved') }}</div>
          </div>
        </div>
        <div class="tech-cal__dialog-foot">
          <div class="tech-cal__dialog-btns">
            <q-btn unelevated class="appt-btn appt-btn--ghost" :label="translate('cancel')" @click="deleteDialogOpen = false"/>
            <q-btn unelevated class="appt-btn appt-btn--danger" :label="translate('delete')" @click="confirmDelete"/>
          </div>
        </div>
      </q-card>
    </q-dialog>
  </div>
</template>

<style scoped>
/* filter fields */

.tech-cal__fields {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.tech-cal__field {
  flex: 1 1 210px;
}

.tech-cal__field--wide {
  flex: 1.7 1 320px;
}

.tech-cal__field :deep([data-field-id="fromDoc"] > div) {
  gap: 0.35rem;
}

/* crew list */

.tech-cal__crew-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tech-cal__crew {
  --crew-color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 9px;
  border-inline-start: 3px solid var(--crew-color);
  font-size: 12.5px;
  color: var(--appt-text-body);
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s ease;
}

.tech-cal__crew:hover {
  background: var(--appt-chip-hover);
}

.tech-cal__crew-check {
  flex: 0 0 auto;
}

.tech-cal__crew--off {
  opacity: 0.55;
}

.tech-cal__crew--off .tech-cal__crew-name {
  text-decoration: line-through;
}

.tech-cal__crew-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tech-cal__crew-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--crew-color, #64748b);
  flex: 0 0 auto;
}

.tech-cal__hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  text-align: center;
  color: var(--appt-text-faint);
  font-size: 12.5px;
  padding: 1rem 0.5rem;
}

/* calendar */

.appt-fc :deep(.tech-cal__fc) {
  height: 100%;
  width: 100%;
  min-width: 0;
}

.tech-cal__overlay {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  text-align: center;
  color: var(--appt-text-muted);
  padding: 1.5rem;
}

.tech-cal__overlay p {
  max-width: 22rem;
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
}

.tech-cal__save-badge {
  margin-inline-start: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}


.tech-cal__ctx-anchor {
  position: fixed;
  width: 0;
  height: 0;
  pointer-events: none;
}

.tech-cal__ctx {
  min-width: 210px;
  border-radius: 12px;
  padding: 6px;
  background: var(--appt-surface);
  color: var(--appt-text);
  border: 1px solid var(--appt-border);
  box-shadow: var(--appt-card-shadow);
}

.tech-cal__ctx-head {
  padding: 6px 10px 8px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--appt-text-faint);
  border-bottom: 1px solid var(--appt-border-soft);
  margin-bottom: 4px;
}

.tech-cal__ctx-item {
  border-radius: 9px;
  min-height: 34px;
  font-size: 12.5px;
  color: var(--appt-text-body);
}

.tech-cal__ctx-item:hover {
  background: var(--appt-chip-hover);
}

.tech-cal__ctx-item--danger {
  color: var(--appt-danger);
}

.tech-cal__ctx-item--blocked {
  opacity: 0.45;
}

.tech-cal__ctx-icon {
  min-width: 26px;
  padding-inline-end: 6px;
}

.tech-cal__ctx-item :deep(.nama-icon:not(.has-fill) path) {
  fill: currentColor;
}

.tech-cal__ctx-empty {
  font-size: 12px;
  color: var(--appt-text-muted);
}

.tech-cal__reason {
  width: min(280px, 92vw);
  padding: 6px 10px 10px;
}

.tech-cal__reason-input :deep(.q-field__control) {
  border-radius: 10px;
}

.tech-cal__reason-input :deep(textarea) {
  min-height: 54px;
  color: var(--appt-text);
}

.tech-cal__reason-input :deep(textarea::placeholder) {
  color: var(--appt-text-faint);
  opacity: 1;
}

.tech-cal__reason-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}

.tech-cal__reason-hint {
  font-size: 11px;
  color: var(--appt-text-faint);
}

.tech-cal__reason-ok.q-btn {
  min-width: 64px;
  min-height: 30px;
  padding: 0 12px;
  font-size: 12px;
}

.tech-cal__toolbar-end {
  display: flex;
  align-items: center;
  gap: 14px;
}

.tech-cal__opt {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--appt-text-muted);
  cursor: pointer;
  user-select: none;
}

.tech-cal__opt-box {
  margin-inline-end: 0;
}

.tech-cal__dirty {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 10px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--appt-accent-soft-border);
  background: var(--appt-accent-soft);
}

.tech-cal__dirty-badge {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border-radius: 9px;
  color: var(--appt-accent);
  background: var(--appt-surface);
  border: 1px solid var(--appt-accent-soft-border);
}

.tech-cal__dirty-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1 1 auto;
}

.tech-cal__dirty-count {
  font-size: 13px;
  font-weight: 600;
  color: var(--appt-text-strong);
}

.tech-cal__dirty-hint {
  font-size: 11px;
  color: var(--appt-text-muted);
}

.tech-cal__dirty-btns {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}


.tech-cal__dialog {
  width: min(420px, 92vw);
  max-width: 92vw;
  border-radius: 16px;
  background: var(--appt-surface);
  color: var(--appt-text);
  border: 1px solid var(--appt-border);
  box-shadow: var(--appt-card-shadow);
  overflow: hidden;
}

.tech-cal__dialog-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--appt-border-soft);
}

.tech-cal__dialog-badge {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: var(--appt-accent-soft);
  border: 1px solid var(--appt-accent-soft-border);
  color: var(--appt-accent);
}

.tech-cal__dialog-badge :deep(.nama-icon:not(.has-fill) path) {
  fill: currentColor;
}

.tech-cal__dialog-badge--danger {
  background: var(--appt-danger-soft);
  border-color: var(--appt-danger-soft-border);
  color: var(--appt-danger);
}

.tech-cal__dialog-heading {
  min-width: 0;
}

.tech-cal__dialog-title {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--appt-text-strong);
  line-height: 1.3;
}

.tech-cal__dialog-sub {
  margin-top: 2px;
  font-size: 12px;
  color: var(--appt-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tech-cal__dialog-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 18px 14px;
  border-top: 1px solid var(--appt-border-soft);
  background: var(--appt-chip-bg);
}

.tech-cal__dialog-btns {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-inline-start: auto;
}

.tech-cal__dialog-btns .appt-btn.q-btn {
  min-height: 34px;
  min-width: 84px;
}

.appt-btn--danger.q-btn {
  background: var(--appt-danger-solid);
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.28);
}

.appt-btn--danger.q-btn--actionable:not(.disabled):hover {
  background: var(--appt-danger-solid-hover);
}

.tech-cal__dialog-search {
  padding: 14px 18px 4px;
}

.tech-cal__dialog-search :deep(.q-field__control) {
  border-radius: 10px;
}

.tech-cal__dialog-search :deep(.q-field__native) {
  color: var(--appt-text);
}

.tech-cal__dialog-search :deep(.q-field__native::placeholder) {
  color: var(--appt-text-faint);
  opacity: 1;
}

.tech-cal__dialog-body {
  max-height: 52vh;
  overflow: auto;
  padding: 8px 18px 14px;
}

.tech-cal__dialog-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0;
}

.tech-cal__dialog-item {
  border-radius: 10px;
  min-height: 42px;
  border: 1px solid transparent;
  color: var(--appt-text-body);
}

.tech-cal__dialog-item:hover {
  background: var(--appt-chip-bg);
  border-color: var(--appt-border);
}

/* status legend */

.tech-cal__legend {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tech-cal__legend-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 9px;
}

.tech-cal__legend-row:hover {
  background: var(--appt-chip-bg);
}

.tech-cal__legend-swatch {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: var(--status-color);
}

.tech-cal__legend-swatch--plain {
  background: transparent;
  border: 1px solid var(--appt-border);
}

.tech-cal__legend-label {
  flex: 1;
  font-size: 12.5px;
  color: var(--appt-text-body);
}

.tech-cal__legend-count {
  font-size: 11.5px;
  color: var(--appt-text-fainter);
  font-family: var(--appt-mono);
}

/* busy and holiday slots — everything else is painted by the Forma theme */

.appt-fc :deep(.tech-cal__busy) {
  opacity: 0.78;
  transition: opacity 0.18s ease-in-out;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.28) 0,
    rgba(255, 255, 255, 0.28) 3px,
    transparent 3px,
    transparent 7px);
}

.appt-fc :deep(.tech-cal__busy:hover) {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .appt-fc :deep(.tech-cal__busy) {
    transition: none;
  }
}

.tw-dark .appt-fc :deep(.tech-cal__busy) {
  opacity: 0.74;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.32) 0,
    rgba(0, 0, 0, 0.32) 3px,
    transparent 3px,
    transparent 7px);
}

/* Crew keeps the fill, status owns the inline-start edge. Colours mirror STATUS_STYLES — a
   stylesheet cannot read the TS palette. Doubled class throughout: the theme's own dark rule for
   .tech-cal__busy is three classes deep, and a single-class rule here would lose to it. */
.appt-fc :deep(.tech-cal__busy.tech-cal__busy--rescheduled) {
  border-inline-start-color: #8b5cf6;
}

.appt-fc :deep(.tech-cal__busy.tech-cal__busy--noshow) {
  border-inline-start-color: #d97706;
}

.appt-fc :deep(.tech-cal__busy.tech-cal__busy--executed) {
  border-inline-start-color: #0f8a53;
}

.appt-fc :deep(.tech-cal__busy.tech-cal__busy--flagged) {
  border-inline-start-width: 8px;
  opacity: 0.92;
}

.appt-fc :deep(.tech-cal__busy.tech-cal__busy--flagged:hover) {
  opacity: 1;
}

.appt-fc :deep(.tech-cal__busy--pending),
.appt-fc :deep(.tech-cal__busy--pending:hover) {
  opacity: 1;
  outline: 2px dashed var(--appt-accent);
  outline-offset: -2px;
}

.appt-fc :deep(.tech-cal__busy--removed),
.appt-fc :deep(.tech-cal__busy--removed:hover) {
  opacity: 0.4;
  text-decoration: line-through;
  filter: grayscale(0.7);
}

.appt-fc :deep(.tech-cal__busy.tech-cal__unavailable),
.appt-fc :deep(.tech-cal__busy.tech-cal__unavailable:hover) {
  opacity: 0.55;
  cursor: not-allowed;
  filter: grayscale(0.55);
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.30) 0,
    rgba(0, 0, 0, 0.30) 4px,
    transparent 4px,
    transparent 9px);
}

.appt-fc :deep(.tech-cal__holiday) {
  opacity: 0.6;
  cursor: default;
  background-color: rgba(239, 68, 68, 0.09);
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(239, 68, 68, 0.20) 0,
    rgba(239, 68, 68, 0.20) 6px,
    transparent 6px,
    transparent 12px);
}

.appt-fc :deep(.tech-cal__past) {
  opacity: 1;
  cursor: not-allowed;
  background-color: rgba(100, 116, 139, 0.14);
}

.tw-dark .appt-fc :deep(.tech-cal__past) {
  background-color: rgba(15, 23, 42, 0.45);
}

.tw-dark .appt-fc :deep(.tech-cal__holiday) {
  background-color: rgba(248, 113, 113, 0.14);
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(248, 113, 113, 0.24) 0,
    rgba(248, 113, 113, 0.24) 6px,
    transparent 6px,
    transparent 12px);
}

/* responsive */

@media (max-width: 1024px) {
  .tech-cal__crew-list {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .tech-cal__crew {
    flex: 1 1 8rem;
  }
}
</style>
